// src/writer/utils/batchGeneration.ts
import { ProjectState } from '../types/project';
import { BookStructureTextElementKey, GenerationContext } from '../types';
import { buildContextualPrompt, buildGenerationContext } from './contextAwareGeneration';

export interface GenerationDependency {
    element: BookStructureTextElementKey;
    dependsOn: BookStructureTextElementKey[];
    priority: number;
    isOptional: boolean;
    condition?: (projectState: ProjectState) => boolean;
}

export interface BatchGenerationProgress {
    totalElements: number;
    completedElements: number;
    currentElement: BookStructureTextElementKey | null;
    failedElements: BookStructureTextElementKey[];
    skippedElements: BookStructureTextElementKey[];
    status: 'preparing' | 'generating' | 'completed' | 'failed' | 'cancelled';
}

export interface BatchGenerationConfig {
    elements: BookStructureTextElementKey[];
    respectDependencies: boolean;
    skipExisting: boolean;
    maxConcurrent: number;
    onProgress?: (progress: BatchGenerationProgress) => void;
    onElementComplete?: (element: BookStructureTextElementKey, result: string) => void;
    onElementFailed?: (element: BookStructureTextElementKey, error: string) => void;
}

// Define generation dependencies and priority order
export const GENERATION_DEPENDENCIES: GenerationDependency[] = [
    // High Priority - Foundation Elements
    { 
        element: 'dedication', 
        dependsOn: [], 
        priority: 1, 
        isOptional: false,
        condition: () => true
    },
    { 
        element: 'epigraph', 
        dependsOn: [], 
        priority: 2, 
        isOptional: true,
        condition: (state) => state.bookMetadata?.fictionVsNonFiction === 'Fiction'
    },
    
    // Medium Priority - Front Matter
    { 
        element: 'preface', 
        dependsOn: ['dedication'], 
        priority: 3, 
        isOptional: true,
        condition: (state) => state.bookMetadata?.fictionVsNonFiction === 'Non-fiction'
    },
    { 
        element: 'acknowledgmentsFront', 
        dependsOn: ['dedication'], 
        priority: 4, 
        isOptional: true,
        condition: () => true
    },
    { 
        element: 'prologue', 
        dependsOn: ['dedication', 'epigraph'], 
        priority: 5, 
        isOptional: true,
        condition: (state) => state.bookMetadata?.fictionVsNonFiction === 'Fiction'
    },
    { 
        element: 'introduction', 
        dependsOn: ['preface'], 
        priority: 5, 
        isOptional: false,
        condition: (state) => state.bookMetadata?.fictionVsNonFiction === 'Non-fiction'
    },
    
    // Low Priority - Back Matter (depends on content completion)
    { 
        element: 'epilogue', 
        dependsOn: ['prologue'], 
        priority: 8, 
        isOptional: true,
        condition: (state) => state.bookMetadata?.fictionVsNonFiction === 'Fiction'
    },
    { 
        element: 'afterword', 
        dependsOn: ['introduction'], 
        priority: 8, 
        isOptional: true,
        condition: (state) => state.bookMetadata?.fictionVsNonFiction === 'Non-fiction'
    },
    { 
        element: 'authorsNote', 
        dependsOn: [], 
        priority: 7, 
        isOptional: true,
        condition: () => true
    },
    { 
        element: 'acknowledgmentsBack', 
        dependsOn: ['acknowledgmentsFront'], 
        priority: 6, 
        isOptional: false,
        condition: () => true
    },
    { 
        element: 'aboutTheAuthor', 
        dependsOn: [], 
        priority: 9, 
        isOptional: false,
        condition: () => true
    },
    { 
        element: 'readingGroupGuide', 
        dependsOn: ['aboutTheAuthor'], 
        priority: 10, 
        isOptional: true,
        condition: (state) => state.bookMetadata?.fictionVsNonFiction === 'Fiction'
    },
    { 
        element: 'teaserNextBook', 
        dependsOn: ['aboutTheAuthor'], 
        priority: 11, 
        isOptional: true,
        condition: () => true
    },
    
    // Very Low Priority - Generated from Content
    { 
        element: 'tableOfContents', 
        dependsOn: [], 
        priority: 12, 
        isOptional: false,
        condition: (state) => (state.chapters?.length || 0) > 0
    },
    { 
        element: 'index', 
        dependsOn: ['tableOfContents'], 
        priority: 13, 
        isOptional: true,
        condition: (state) => state.bookMetadata?.fictionVsNonFiction === 'Non-fiction'
    }
];

// Get filtered elements based on project state
export const getApplicableElements = (
    projectState: ProjectState,
    requestedElements?: BookStructureTextElementKey[]
): BookStructureTextElementKey[] => {
    const applicable = GENERATION_DEPENDENCIES
        .filter(dep => dep.condition ? dep.condition(projectState) : true)
        .map(dep => dep.element);
    
    if (requestedElements) {
        return applicable.filter(element => requestedElements.includes(element));
    }
    
    return applicable;
};

// Calculate generation order based on dependencies
export const calculateGenerationOrder = (
    elements: BookStructureTextElementKey[],
    respectDependencies: boolean = true
): BookStructureTextElementKey[] => {
    if (!respectDependencies) {
        // Sort by priority only
        return elements.sort((a, b) => {
            const depA = GENERATION_DEPENDENCIES.find(d => d.element === a);
            const depB = GENERATION_DEPENDENCIES.find(d => d.element === b);
            return (depA?.priority || 999) - (depB?.priority || 999);
        });
    }
    
    const ordered: BookStructureTextElementKey[] = [];
    const remaining = [...elements];
    const processing = new Set<BookStructureTextElementKey>();
    
    const canGenerate = (element: BookStructureTextElementKey): boolean => {
        const dependency = GENERATION_DEPENDENCIES.find(d => d.element === element);
        if (!dependency) return true;
        
        return dependency.dependsOn.every(dep => 
            ordered.includes(dep) || !elements.includes(dep)
        );
    };
    
    // Resolve dependencies
    while (remaining.length > 0) {
        const ready = remaining.filter(element => 
            !processing.has(element) && canGenerate(element)
        );
        
        if (ready.length === 0) {
            // No dependencies can be resolved, add remaining by priority
            const byPriority = remaining.sort((a, b) => {
                const depA = GENERATION_DEPENDENCIES.find(d => d.element === a);
                const depB = GENERATION_DEPENDENCIES.find(d => d.element === b);
                return (depA?.priority || 999) - (depB?.priority || 999);
            });
            ordered.push(...byPriority);
            break;
        }
        
        // Sort ready elements by priority
        ready.sort((a, b) => {
            const depA = GENERATION_DEPENDENCIES.find(d => d.element === a);
            const depB = GENERATION_DEPENDENCIES.find(d => d.element === b);
            return (depA?.priority || 999) - (depB?.priority || 999);
        });
        
        // Add the highest priority ready element
        const next = ready[0];
        ordered.push(next);
        remaining.splice(remaining.indexOf(next), 1);
    }
    
    return ordered;
};

// Check if element already exists
export const elementExists = (
    element: BookStructureTextElementKey,
    projectState: ProjectState
): boolean => {
    const frontMatterKey = element as keyof typeof projectState.frontMatter;
    const backMatterKey = element as keyof typeof projectState.backMatter;
    
    if (projectState.frontMatter?.[frontMatterKey]) {
        const value = projectState.frontMatter[frontMatterKey];
        return typeof value === 'string' && value.trim().length > 0;
    }
    
    if (projectState.backMatter?.[backMatterKey]) {
        const value = projectState.backMatter[backMatterKey];
        return typeof value === 'string' && value.trim().length > 0;
    }
    
    return false;
};

// Generate batch processing plan
export const createBatchGenerationPlan = (
    projectState: ProjectState,
    config: BatchGenerationConfig
): {
    orderedElements: BookStructureTextElementKey[];
    toGenerate: BookStructureTextElementKey[];
    toSkip: BookStructureTextElementKey[];
    estimatedTime: number;
} => {
    const applicableElements = getApplicableElements(projectState, config.elements);
    const orderedElements = calculateGenerationOrder(applicableElements, config.respectDependencies);
    
    const toSkip: BookStructureTextElementKey[] = [];
    const toGenerate: BookStructureTextElementKey[] = [];
    
    orderedElements.forEach(element => {
        if (config.skipExisting && elementExists(element, projectState)) {
            toSkip.push(element);
        } else {
            toGenerate.push(element);
        }
    });
    
    // Estimate 15-30 seconds per element for AI generation
    const estimatedTime = toGenerate.length * 22; // seconds
    
    return {
        orderedElements,
        toGenerate,
        toSkip,
        estimatedTime
    };
};

// Execute batch generation
export const executeBatchGeneration = async (
    projectState: ProjectState,
    config: BatchGenerationConfig,
    generateFunction: (element: BookStructureTextElementKey, prompt: string) => Promise<string>
): Promise<BatchGenerationProgress> => {
    const plan = createBatchGenerationPlan(projectState, config);
    const { toGenerate } = plan;
    
    const progress: BatchGenerationProgress = {
        totalElements: toGenerate.length,
        completedElements: 0,
        currentElement: null,
        failedElements: [],
        skippedElements: plan.toSkip,
        status: 'preparing'
    };
    
    // Report initial progress
    config.onProgress?.(progress);
    
    if (toGenerate.length === 0) {
        progress.status = 'completed';
        config.onProgress?.(progress);
        return progress;
    }
    
    progress.status = 'generating';
    const context = buildGenerationContext(projectState);
    
    // Process elements in order
    for (const element of toGenerate) {
        progress.currentElement = element;
        config.onProgress?.(progress);
        
        try {
            // Get base prompt for element (would need to be implemented)
            const basePrompt = getBasePromptForElement(element);
            const contextualPrompt = buildContextualPrompt(element, basePrompt, context);
            
            const result = await generateFunction(element, contextualPrompt);
            
            progress.completedElements++;
            config.onElementComplete?.(element, result);
            
        } catch (error) {
            progress.failedElements.push(element);
            config.onElementFailed?.(element, error instanceof Error ? error.message : 'Unknown error');
        }
        
        config.onProgress?.(progress);
    }
    
    progress.currentElement = null;
    progress.status = progress.failedElements.length > 0 ? 'failed' : 'completed';
    config.onProgress?.(progress);
    
    return progress;
};

// Get base prompt for element (simplified - would need full implementation)
const getBasePromptForElement = (element: BookStructureTextElementKey): string => {
    const prompts: Record<BookStructureTextElementKey, string> = {
        'dedication': 'Write a heartfelt dedication for this book. Keep it personal and meaningful.',
        'epigraph': 'Create an appropriate epigraph (quote or short passage) that sets the tone for the book.',
        'preface': 'Write a preface explaining the author\'s motivation and approach to writing this book.',
        'acknowledgmentsFront': 'Write acknowledgments thanking those who helped with the creation of this book.',
        'prologue': 'Write a compelling prologue that sets up the story and engages readers.',
        'introduction': 'Write an informative introduction that outlines the book\'s purpose and scope.',
        'epilogue': 'Write an epilogue that provides closure and shows the outcome of the story.',
        'afterword': 'Write an afterword with the author\'s final thoughts and reflections.',
        'authorsNote': 'Write an author\'s note sharing insights about the writing process.',
        'acknowledgmentsBack': 'Write comprehensive acknowledgments for the back matter.',
        'aboutTheAuthor': 'Write a professional author biography highlighting relevant credentials and experience.',
        'readingGroupGuide': 'Create discussion questions and topics for book clubs and reading groups.',
        'teaserNextBook': 'Write an engaging teaser or excerpt from the next book.',
        'tableOfContents': 'Generate a properly formatted table of contents with chapter titles and page numbers.',
        'index': 'Create a comprehensive index for this book with important terms and page references.',
        'isbn': '',
        'publisher': '',
        'edition': '',
        'copyrightNotice': '',
        'titleImagePrompt': '',
        'backCoverImagePrompt': ''
    };
    
    return prompts[element] || `Generate appropriate content for ${element}.`;
};

// Get suggested batch configurations
export const getSuggestedBatchConfigs = (projectState: ProjectState) => {
    const isFirstTime = !projectState.frontMatter || Object.keys(projectState.frontMatter).length === 0;
    const isFiction = projectState.bookMetadata?.fictionVsNonFiction === 'Fiction';
    
    return {
        essential: {
            name: 'Essential Elements',
            description: 'Core elements every book needs',
            elements: ['dedication', 'acknowledgmentsBack', 'aboutTheAuthor'] as BookStructureTextElementKey[],
            estimatedTime: '2-3 minutes'
        },
        complete: {
            name: 'Complete Book Structure',
            description: 'All applicable elements for a professional book',
            elements: getApplicableElements(projectState),
            estimatedTime: '8-12 minutes'
        },
        fiction: {
            name: 'Fiction Package',
            description: 'Elements specifically for fiction books',
            elements: ['dedication', 'epigraph', 'prologue', 'epilogue', 'acknowledgmentsBack', 'aboutTheAuthor', 'readingGroupGuide'] as BookStructureTextElementKey[],
            estimatedTime: '5-7 minutes'
        },
        nonFiction: {
            name: 'Non-Fiction Package',
            description: 'Elements for non-fiction and educational books',
            elements: ['dedication', 'preface', 'introduction', 'acknowledgmentsBack', 'aboutTheAuthor', 'index'] as BookStructureTextElementKey[],
            estimatedTime: '5-7 minutes'
        }
    };
};